import { useMemo, useState, useLayoutEffect } from 'react';
import type { RefObject } from 'react';
import type { QuoteProductDTO } from '@setvi/shared/services/react-query';

const PRODUCT_ROW_HEIGHT = 130;
const PRODUCT_ROW_HEIGHT_WITH_NOTE = 146;
const OPTION_ROW_HEIGHT = 130;
const OPTION_ROW_HEIGHT_WITH_BADGE = 134;
const OPTION_ROW_HEIGHT_WITH_NOTE = 146;
const OPTION_ROW_HEIGHT_WITH_BADGE_AND_NOTE = 160;
const OVERSCAN = 2;
const GAP = 12;
const SECTION_TOP_PADDING = 12;
const SECTION_BOTTOM_PADDING = 12;

interface UseInnerVirtualizerOptions {
  products: QuoteProductDTO[];
  scrollContainerRef: RefObject<HTMLDivElement>;
  sectionStartOffset: number;
  sectionHeaderHeight: number;
  enabled: boolean;
}

interface VirtualItem {
  index: number;
  start: number;
  size: number;
  key: string;
}

const getOptionRowHeight = (option: QuoteProductDTO): number => {
  const hasNote = !!option?.note?.trim();
  const hasBadge = !!option?.labelType;
  if (hasNote)
    return hasBadge
      ? OPTION_ROW_HEIGHT_WITH_BADGE_AND_NOTE
      : OPTION_ROW_HEIGHT_WITH_NOTE;
  return hasBadge ? OPTION_ROW_HEIGHT_WITH_BADGE : OPTION_ROW_HEIGHT;
};

const getProductHeight = (product: QuoteProductDTO) => {
  const baseHeight = product?.note?.trim()
    ? PRODUCT_ROW_HEIGHT_WITH_NOTE
    : PRODUCT_ROW_HEIGHT;
  const options = product?.children ?? [];
  let optionsHeight = 0;
  for (let i = 0; i < options.length; i += 1) {
    optionsHeight += getOptionRowHeight(options[i]);
  }
  return baseHeight + optionsHeight;
};

export const useInnerVirtualizer = ({
  products,
  scrollContainerRef,
  sectionStartOffset,
  sectionHeaderHeight,
  enabled
}: UseInnerVirtualizerOptions) => {
  const [scrollTop, setScrollTop] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);

  // Calculate total size and item positions
  const { totalSize, itemPositions } = useMemo(() => {
    if (!enabled || products.length === 0) {
      return { totalSize: 0, itemPositions: [] as number[] };
    }

    const positions: number[] = [];
    let offset = SECTION_TOP_PADDING;

    for (let i = 0; i < products.length; i += 1) {
      positions.push(offset);
      offset += getProductHeight(products[i]);
      // Add gap after each item except the last
      if (i < products.length - 1) {
        offset += GAP;
      }
    }

    return {
      totalSize: offset + SECTION_BOTTOM_PADDING,
      itemPositions: positions
    };
  }, [products, enabled]);

  // Listen to scroll events
  useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return undefined;

    const handleScroll = () => {
      setScrollTop(container.scrollTop);
      setContainerHeight(container.clientHeight);
    };

    // Initial values
    handleScroll();

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [scrollContainerRef]);

  // Calculate visible items based on scroll position
  const virtualItems = useMemo((): VirtualItem[] => {
    if (!enabled || products.length === 0 || containerHeight === 0) {
      return [];
    }

    const sectionTop = sectionStartOffset + sectionHeaderHeight;
    const viewportTop = scrollTop;
    const viewportBottom = scrollTop + containerHeight;

    const items: VirtualItem[] = [];

    for (let i = 0; i < products.length; i += 1) {
      const itemStart = itemPositions[i];
      const itemSize = getProductHeight(products[i]);
      const itemEnd = itemStart + itemSize;

      // Item's absolute position in the scroll container
      const absoluteStart = sectionTop + itemStart;
      const absoluteEnd = sectionTop + itemEnd;

      // Check if item is in or near viewport (with overscan)
      const overscanPx = OVERSCAN * PRODUCT_ROW_HEIGHT;
      const isVisible =
        absoluteEnd >= viewportTop - overscanPx &&
        absoluteStart <= viewportBottom + overscanPx;

      if (isVisible) {
        items.push({
          index: i,
          start: itemStart,
          size: itemSize,
          key: products[i].id || products[i]._id || String(i)
        });
      }
    }

    return items;
  }, [
    enabled,
    products,
    containerHeight,
    scrollTop,
    sectionStartOffset,
    sectionHeaderHeight,
    itemPositions
  ]);

  return {
    virtualItems,
    totalSize
  };
};
